export type TimerPosition = [number, number];

/** How old a GPS fix may be before it stops standing in for "where we are
 *  now". Shared so callers that resolve a position themselves apply the same
 *  bound as FlightTimerService does. */
export const MAX_POSITION_AGE_MS = 60_000;

export function isValidTimerPosition(pos: unknown): pos is TimerPosition {
  if (!Array.isArray(pos) || pos.length !== 2) {
    return false;
  }

  const lon = pos[0];
  const lat = pos[1];

  if (!Number.isFinite(lon) || !Number.isFinite(lat)) {
    return false;
  }

  if (lon < -180 || lon > 180 || lat < -90 || lat > 90) {
    return false;
  }

  // 0 latitude OR 0 longitude can be perfectly valid. Only Null Island
  // ([0, 0]) is treated as the GPS-loss sentinel used by the EFB.
  return !(lon === 0 && lat === 0);
}

// Remembers the last real fix so a temporary GPS loss cannot stamp [0, 0]
// onto a takeoff or landing. Age-bounded on purpose: the flight log is a
// record, so a stale fix from an earlier leg is worse than no position.
export class TimerPositionCache {
  private value: TimerPosition | null = null;
  private at: number = 0;

  /**
   * `at` is when the fix was *measured*, not when it was handed to us. Callers
   * replaying a stored or backdated position must pass it, or an old fix is
   * laundered into the cache as fresh and outlives its own age bound.
   */
  public remember(pos: unknown, at: number = Date.now()): void {
    if (!isValidTimerPosition(pos)) return;
    // A stamp that is not a real time tells us nothing about freshness, and
    // caching under it would make the entry impossible to age out correctly.
    if (!Number.isFinite(at)) return;
    // Monotonic while the cached entry is still usable, so a replayed fix
    // (restart recovery, backdated departure detection) cannot rewind the cache
    // and evict a fresher one.
    //
    // Only while it is usable, though. A cached entry that is not itself fresh
    // has no claim to be kept: if it is stale, or dated in the future because
    // the device clock was corrected backwards, then holding on to it would
    // freeze the cache on a position that can never age out while rejecting
    // every real fix arriving with a now-smaller timestamp. Rebase instead.
    if (at <= this.at && isFreshFix(this.at, MAX_POSITION_AGE_MS)) return;
    this.value = [pos[0], pos[1]];
    this.at = at;
  }

  public get(maxAgeMs: number): TimerPosition | undefined {
    if (!this.value) return undefined;
    // isFreshFix, not a bare `age > maxAgeMs`: a future-dated entry has a
    // negative age, which a one-sided test reads as brand new forever.
    if (!isFreshFix(this.at, maxAgeMs)) return undefined;
    return [this.value[0], this.value[1]];
  }

  public ageMs(): number {
    if (!this.value) return Infinity;
    return Date.now() - this.at;
  }
}

/** True when `timestamp` is within `maxAgeMs` of `now`. A future-dated
 *  timestamp counts as stale: a clock that disagrees is not evidence of
 *  freshness. */
export function isFreshFix(timestamp: number, maxAgeMs: number, now: number = Date.now()): boolean {
  if (!Number.isFinite(timestamp)) return false;
  const ageMs = now - timestamp;
  return ageMs >= 0 && ageMs <= maxAgeMs;
}

/** True when the event being recorded is too old to be described by a
 *  position resolved now.
 *
 *  `undefined` means the caller has no event time to offer and the age of the
 *  event is simply unknown, so it does not block a fallback. A non-positive or
 *  non-finite time is different: that is a real value that is wrong (an
 *  unset struct field, a clock fault), and it does block one. A future-dated
 *  time is not historic. */
export function isHistoricEvent(eventTime: number | undefined, maxAgeMs: number, now: number = Date.now()): boolean {
  if (eventTime === undefined) return false;
  if (!Number.isFinite(eventTime) || eventTime <= 0) return true;
  return now - eventTime > maxAgeMs;
}
